

import { Component, Vue } from "vue-property-decorator"
import { Getter, Mutation, State, Action } from "vuex-class"

import api from "@/services/api"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import { BModal, BIconEmojiNeutralFill } from "bootstrap-vue"
import { UserSettings, CaseCloseOption, ViewsOpeningOption } from "@/types/userSettings"
import DataForm from "./Config/DataForm.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import { TimeZone } from "@/types/timeZone"
import cloneDeep from "lodash/cloneDeep"

@Component({
	components: {
		"userSettingsModal": BModal,
		"scrollbar": VuePerfectScrollbar,
		"data-form": DataForm
	}
})
export default class UserSettingsModal extends Vue {
	@State tenantEnabledFeatures!: { [key: string]: boolean }

	@Getter("getUserSettings")
	public userSettings!: UserSettings;

	@Getter("getRecordOrCaseName")
	recordOrCaseName!: string

	@State displayTimezone!: TimeZone | BIconEmojiNeutralFill

	@Mutation public setUserSettings!: (userSettings: UserSettings) => void;

	@Action setDisplayTimezone!: (timezone: TimeZone | null) => void

	private isOpen: boolean = false;
	private editSettings: UserSettings | null = null

	public get viewsOpeningOptions (): any[] {
		if (!this.userSettings) {
			return []
		}

		return [
			{
				id: ViewsOpeningOption.SameTab,
				title: "Same tab"
			},
			{
				id: ViewsOpeningOption.NewTab,
				title: "New tab"
			}
		]
	}

	public get caseCloseOptions (): any[] {
		if (!this.userSettings) {
			return []
		}

		return [
			{
				id: CaseCloseOption.StayOnCase,
				title: `Remain on ${this.recordOrCaseName}`
			},
			{
				id: CaseCloseOption.RedirectToDashboard,
				title: "Navigate to Dashboard"
			}
		]
	}

	public get columns (): any[] {
		if (!this.userSettings) {
			return []
		}

		return [
			{
				key: "settings.viewFoldersExpanded",
				label: "Automatically expand folders in Views",
				dataType: "checkbox",
				valueColumn: "settings.viewFoldersExpanded"
			},
			{
				key: "settings.scheduleFoldersExpanded",
				label: "Automatically expand folders in Schedules",
				dataType: "checkbox",
				valueColumn: "settings.scheduleFoldersExpanded"
			},
			{
				key: "settings.viewsOpening",
				label: `Opening a ${this.recordOrCaseName} from Insights & Views`,
				dataType: "list",
				valueColumn: "settings.viewsOpening",
				options: this.viewsOpeningOptions,
				placeholder: "New Tab",
				tooltip: `Affects how a ${this.recordOrCaseName} in Insights and Views will be opened when clicked on. The opposite setting will happen when ctrl + clicking instead.`
			},
			{
				key: "settings.caseClose",
				label: `After closing a ${this.recordOrCaseName}`,
				dataType: "list",
				valueColumn: "settings.caseClose",
				options: this.caseCloseOptions,
				placeholder: "Navigate to Dashboard",
				inModal: () => this.tenantEnabledFeatures[TenantFeatures.StayOnCaseAfterClosure],
				tooltip: `Determines what happens after closing a ${this.recordOrCaseName}. Either remain on the current ${this.recordOrCaseName} or be redirected to your dashboard.`
			},
			{
				key: "settings.showTimezoneSelect",
				label: "Allow setting of page timezone",
				dataType: "checkbox",
				inModal: () => this.tenantEnabledFeatures[TenantFeatures.UiTimezone],
				tooltip: "Allow manual selection of timezone for displaying times"
			},
			{
				key: "settings.highLatencyMode",
				label: "Enable high latency mode",
				dataType: "checkbox",
				inModal: () => this.tenantEnabledFeatures[TenantFeatures.UserRoleFieldHighLatencyUserEnhancements],
				tooltip: "Enable high latency user mode to safeguard against unexpected values being entered"
			}
		]
	}

	public open () {
		this.editSettings = cloneDeep(this.userSettings)
	}
	public close () {
		this.editSettings = null
	}

	public async updateUser () {
		if (!this.editSettings) return

		await api.userSettingsSave(this.editSettings)

		// If timezone display has been unset, clear timezone
		if (this.editSettings &&
			!this.editSettings.settings["showTimezoneSelect"] &&
			this.displayTimezone) {
			this.setDisplayTimezone(null)
		}

		this.setUserSettings(this.editSettings)

		this.close()
	}
}
